import { sharedApi, getAuthToken } from "../../../services/sharedApi";

import type {
  DictationSessionRequest,
  DictationSessionResponse,
  DictationSubmissionRequest,
  DictationResult,
  DictationSettings,
  DictationStats,
  DictationHistoryItem,
} from "../../../types/dictation";

export class DictationService {
  private transformResult(raw: Record<string, unknown>): DictationResult {
    // Provide fallbacks for extended fields expected by client types
    return {
      sessionId: raw.sessionId as string,
      baseAccuracy: (raw.baseAccuracy ?? raw.finalAccuracy ?? 0) as number,
      finalAccuracy: (raw.finalAccuracy ?? raw.baseAccuracy ?? 0) as number,
      totalRepeats: (raw.totalRepeats ?? 0) as number,
      textMarkup: (raw.textMarkup ?? []) as unknown[],
      smartComparison: (raw.smartComparison ?? {
        sentences: [],
        overallAccuracy: (raw.finalAccuracy ?? 0) as number,
        totalCorrectWords: 0,
        totalExpectedWords: 0,
        summary: { perfectSentences: 0, totalSentences: 0, averageAccuracy: 0 },
      }) as DictationResult["smartComparison"],
      timeSpentMs: (raw.timeSpentMs ?? 0) as number,
      wordsPerMinute: raw.wordsPerMinute as number | undefined,
    } as DictationResult;
  }

  private transformStats(raw: Record<string, unknown>): DictationStats {
    return {
      totalSessions: (raw.totalSessions ??
        raw.totalSessionsCount ??
        0) as number,
      completedSessions: (raw.completedSessions ??
        raw.totalSessions ??
        0) as number,
      averageAccuracy: (raw.averageAccuracy ?? 0) as number,
      bestAccuracy: (raw.bestAccuracy ?? raw.averageAccuracy ?? 0) as number,
      totalTimeSpent: (raw.totalTimeSpent ?? 0) as number,
      averageWordsPerMinute: raw.averageWordsPerMinute as number | undefined,
      totalRepeatsUsed: (raw.totalRepeatsUsed ?? 0) as number,
      perfectSessions: (raw.perfectSessions ?? 0) as number,
    } as DictationStats;
  }

  private transformHistory(
    items: Record<string, unknown>[],
  ): DictationHistoryItem[] {
    return items.map((h) => ({
      id: h.id as string,
      textTitle: (h.textTitle ?? h.textId ?? "") as string,
      language: h.language as string,
      level: (h.level ?? "") as string,
      completedAt: (h.completedAt ??
        h.createdAt ??
        new Date().toISOString()) as string,
      finalAccuracy: (h.finalAccuracy ?? h.accuracy ?? 0) as number,
      totalRepeats: (h.totalRepeats ?? 0) as number,
      timeSpentMs: (h.timeSpentMs ?? h.duration ?? 0) as number,
    }));
  }
  /**
   * Начинает новую сессию диктанта
   */
  async startSession(
    request: DictationSessionRequest = {},
  ): Promise<DictationSessionResponse> {
    const token = getAuthToken();
    return (
      sharedApi as Record<
        string,
        Record<string, (...args: unknown[]) => unknown>
      >
    ).dictation.start(request, token) as Promise<DictationSessionResponse>;
  }

  /**
   * Отправляет результат диктанта
   */
  async submitDictation(
    submission: DictationSubmissionRequest,
  ): Promise<DictationResult> {
    const token = getAuthToken();
    const raw = (await (
      sharedApi as Record<
        string,
        Record<string, (...args: unknown[]) => unknown>
      >
    ).dictation.submit(submission, token)) as Record<string, unknown>;
    return this.transformResult(raw);
  }

  /**
   * Получает статистику диктантов
   */
  async getStats(): Promise<DictationStats> {
    const token = getAuthToken();
    const raw = (await (
      sharedApi as Record<
        string,
        Record<string, (...args: unknown[]) => unknown>
      >
    ).dictation.getStats(token)) as Record<string, unknown>;
    return this.transformStats(raw);
  }

  /**
   * Получает историю диктантов
   */
  async getHistory(limit = 20, offset = 0): Promise<DictationHistoryItem[]> {
    const token = getAuthToken();
    const raw = (await (
      sharedApi as Record<
        string,
        Record<string, (...args: unknown[]) => unknown>
      >
    ).dictation.getHistory(limit, offset, token)) as Record<string, unknown>[];
    return this.transformHistory(raw);
  }

  /**
   * Получает настройки диктанта
   */
  async getSettings(): Promise<DictationSettings> {
    const token = getAuthToken();
    return (
      sharedApi as Record<
        string,
        Record<string, (...args: unknown[]) => unknown>
      >
    ).dictation.getSettings(token) as Promise<DictationSettings>;
  }

  /**
   * Обновляет настройки диктанта
   */
  async updateSettings(settings: Partial<DictationSettings>): Promise<void> {
    const token = getAuthToken();
    await (
      sharedApi as Record<
        string,
        Record<string, (...args: unknown[]) => unknown>
      >
    ).dictation.updateSettings(settings, token);
  }

  /**
   * Проверяет доступность диктанта (лимиты)
   */
  async checkEligibility(): Promise<{
    eligible: boolean;
    unlimited: boolean;
    limit: number;
    used: number;
    remaining: number;
    reason: string;
  }> {
    const token = getAuthToken();
    return (
      sharedApi as Record<
        string,
        Record<string, (...args: unknown[]) => unknown>
      >
    ).dictation.checkEligibility(token) as Promise<{
      eligible: boolean;
      unlimited: boolean;
      limit: number;
      used: number;
      remaining: number;
      reason: string;
    }>;
  }
}

export const dictationService = new DictationService();
